/**
 * Utilities for interpreting API errors, as defined in our API codebase
 * at https://github.com/butlrtechnologies/butlr-api-container/blob/main/pkg/common/butlrerror/error.go
 * (as of writing)
 */

import { GraphQLError } from 'graphql';

/**
 * Specific error codes that the frontend keys behavior off (not just the
 * group prefix). Add one here when a code needs more than the group-level
 * helpers (`isErrorNotFound` etc.) — so the frontend has exactly one
 * place to update if the backend ever renumbers. Mirrors
 * butlr-api-container/pkg/common/butlrerror/codes.go.
 */
export const ERROR_USER_NOT_FOUND = 40500;

/**
 * True iff the GraphQL error is the auth-API's "no idps_users row for
 * this JWT subject" signal. Used by SpaceAccessContext's circuit breaker
 * to recognize legacy users who pre-date User Management and grant them
 * unrestricted access until a real grant is created.
 */
export function isErrorUserNotFound(error: GraphQLError): boolean {
  return error.extensions?.code === ERROR_USER_NOT_FOUND;
}

/**
 * Our error codes are 5-digit numbers which begin
 * with an HTTP code prefix. This function extracts
 * the HTTP code from the error code.
 */
export function getErrorCodeGroup(error: GraphQLError): number | null {
  const code = error.extensions?.code;
  if (!code || typeof code !== 'number') {
    return null;
  }
  return Math.floor(code / 100);
}

export function isErrorNotAuthenticated(error: GraphQLError) {
  return getErrorCodeGroup(error) === 401;
}

export function isErrorBadRequest(error: GraphQLError) {
  return getErrorCodeGroup(error) === 400;
}

export function isErrorNotFound(error: GraphQLError) {
  return getErrorCodeGroup(error) === 404;
}

export function isErrorConflict(error: GraphQLError) {
  return getErrorCodeGroup(error) === 409;
}

export function isErrorInternal(error: GraphQLError) {
  return getErrorCodeGroup(error) === 500;
}
